import { Space } from '../../models/asset.model';
import { GooglePlaceDetailsDto } from '../../models/google-maps-link-resolve.model';
import { ResolvedAddress } from '../../models/resolved-address.model';
import { phoneFromPlaceDetails, websiteFromPlaceDetails } from '../utils/contact.utils';
import { weeklyHoursFromGoogleOpeningHours } from './opening-hours.utils';
import { slugifyLocation } from '../utils/slugify.utils';
import { GoogleAddressComponent, GooglePlaceResult } from './google-place.types';

function component(
  components: GoogleAddressComponent[],
  ...types: string[]
): GoogleAddressComponent | undefined {
  for (const type of types) {
    const hit = components.find(c => c.types.includes(type));
    if (hit?.long_name?.trim()) return hit;
  }
  return undefined;
}

function streetLine(components: GoogleAddressComponent[]): string {
  const route = component(components, 'route')?.long_name ?? '';
  const number = component(components, 'street_number')?.long_name ?? '';
  if (route && number) return `${route} ${number}`.trim();
  if (route) return route;
  return '';
}

/** District / barrio with city fallback for rural or foreign addresses. */
export function resolveDistrict(components: GoogleAddressComponent[]): string {
  const district = component(
    components,
    'sublocality_level_1',
    'sublocality',
    'neighborhood',
    'administrative_area_level_3',
  )?.long_name?.trim();

  if (district) return district;

  return component(components, 'locality', 'postal_town', 'administrative_area_level_2')
    ?.long_name?.trim() ?? '';
}

export function resolveProvince(components: GoogleAddressComponent[]): string {
  return (
    component(components, 'administrative_area_level_2', 'administrative_area_level_1')
      ?.long_name?.trim() ?? ''
  );
}

export function buildSlugCompleto(
  countryCode: string,
  slugProvincia: string,
  slugDistrito: string,
  spaceNameSlug: string,
): string {
  const prov = slugProvincia.trim();
  const dist = slugDistrito.trim();
  const space = spaceNameSlug.trim();
  if (!prov || !dist || !space) return '';

  const segments = [prov, dist, space];
  const cc = countryCode.trim().toUpperCase();
  if (cc === 'ES') return segments.join('/');
  return [cc.toLowerCase(), ...segments].join('/');
}

/** Maps server Place Details JSON into our normalized address model. */
export function parsePlaceDetailsDto(
  dto: GooglePlaceDetailsDto,
  spaceName: string,
): ResolvedAddress | null {
  const lat = dto.geometry?.location?.lat;
  const lng = dto.geometry?.location?.lng;
  const place: GooglePlaceResult = {
    place_id: dto.place_id,
    formatted_address: dto.formatted_address,
    address_components: dto.address_components,
    geometry:
      typeof lat === 'number' && typeof lng === 'number'
        ? {
            location: {
              lat: () => lat,
              lng: () => lng,
            },
          }
        : undefined,
  };

  const resolved = parseGooglePlace(place, spaceName);
  if (!resolved) return null;

  const officialName = dto.name?.trim();
  const phone = phoneFromPlaceDetails(dto);
  const website = websiteFromPlaceDetails(dto);
  const weeklyHours = weeklyHoursFromGoogleOpeningHours(dto.opening_hours);

  return {
    ...resolved,
    ...(officialName ? { officialName } : {}),
    ...(phone ? { phone } : {}),
    ...(website ? { website } : {}),
    ...(weeklyHours ? { weeklyHours } : {}),
  };
}

export function parseGooglePlace(
  place: GooglePlaceResult,
  spaceName: string,
  officialNameFromPlace?: string,
): ResolvedAddress | null {
  const components = place.address_components ?? [];
  if (!components.length) return null;

  const countryComp = component(components, 'country');
  const country = countryComp?.long_name?.trim() ?? '';
  const countryCode = countryComp?.short_name?.trim().toUpperCase() ?? '';
  const province = resolveProvince(components);
  const district = resolveDistrict(components);
  const postalCode = component(components, 'postal_code')?.long_name?.trim() ?? '';

  if (!country || !province || !district) return null;

  const formattedAddress = place.formatted_address?.trim()
    ?? [streetLine(components), district, province, country].filter(Boolean).join(', ');

  const addressLine = streetLine(components) || formattedAddress;
  const slugProvincia = slugifyLocation(province);
  const slugDistrito = slugifyLocation(district);
  const spaceNameSlug = slugifyLocation(spaceName);
  const slugCompleto = buildSlugCompleto(countryCode, slugProvincia, slugDistrito, spaceNameSlug);

  const lat = place.geometry?.location?.lat?.();
  const lng = place.geometry?.location?.lng?.();

  const officialName = officialNameFromPlace?.trim()
    || (place as GooglePlaceResult & { name?: string }).name?.trim();

  return {
    formattedAddress,
    addressLine,
    country: country === 'Spain' ? 'España' : country,
    countryCode,
    province,
    district,
    postalCode,
    slugProvincia,
    slugDistrito,
    slugCompleto,
    placeId: place.place_id ?? '',
    officialName: officialName || undefined,
    latitude: typeof lat === 'number' && Number.isFinite(lat) ? lat : undefined,
    longitude: typeof lng === 'number' && Number.isFinite(lng) ? lng : undefined,
  };
}

/** Short location line for listing cards (explore, landing, space-card). */
export function spaceLocationLabel(
  space: Pick<Space, 'address' | 'formattedAddress' | 'province' | 'district'>,
): string {
  const district = space.district?.trim();
  const province = space.province?.trim();
  if (district && province) {
    return `${province} · ${district}`;
  }
  if (district) return district;
  if (province) return province;
  const addr = space.formattedAddress?.trim() || space.address?.trim();
  if (addr) {
    const first = addr.split(',')[0]?.trim();
    return first && first.length > 0 ? first : addr;
  }
  return 'España';
}

export function resolvedAddressFromSpace(
  space: Pick<
    import('../../models/asset.model').Space,
    | 'address'
    | 'formattedAddress'
    | 'postalCode'
    | 'country'
    | 'countryCode'
    | 'province'
    | 'district'
    | 'slugProvincia'
    | 'slugDistrito'
    | 'slugCompleto'
    | 'googlePlaceId'
    | 'phone'
    | 'website'
    | 'latitude'
    | 'longitude'
  >,
  spaceName: string,
): ResolvedAddress | null {
  if (!space.province?.trim() || !space.district?.trim()) return null;

  const slugProvincia = space.slugProvincia?.trim() || slugifyLocation(space.province);
  const slugDistrito = space.slugDistrito?.trim() || slugifyLocation(space.district);
  const spaceNameSlug = slugifyLocation(spaceName);
  const countryCode = space.countryCode?.trim().toUpperCase() ?? 'ES';

  return {
    formattedAddress: space.formattedAddress?.trim() || space.address,
    addressLine: space.address,
    country: space.country ?? '',
    countryCode,
    province: space.province,
    district: space.district,
    postalCode: space.postalCode ?? '',
    slugProvincia,
    slugDistrito,
    slugCompleto: space.slugCompleto?.trim()
      || buildSlugCompleto(countryCode, slugProvincia, slugDistrito, spaceNameSlug),
    placeId: space.googlePlaceId ?? '',
    phone: space.phone,
    website: space.website,
    latitude: space.latitude,
    longitude: space.longitude,
  };
}
